// src/App.jsx
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { DeviceProvider } from './context/DeviceContext';
import { JsonLdProvider } from './context/JsonLdContext';
import 'react-toastify/dist/ReactToastify.css';
import Header from './components/Header';
import Footer from './components/Footer';
import CombinedJsonLd from './components/SEO/CombinedJsonLd';
import Meta from './components/Meta';
import ReactGA from 'react-ga4';
import { FRONTEND_BASE_URL, GA_TRACKING_ID, GA_DEBUG } from './constants/constants';

// ❶ Redux と RTK Query フック、アクションをインポート
import { useSelector, useDispatch } from 'react-redux';
import { useGetUsedItemsQuery } from './slices/subscriptionsApiSlice';
import { updateUsedItems } from './slices/authSlice';

const App = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  // ❷ Redux store から userInfo を取得
  const userInfo = useSelector((state) => state.auth.userInfo);

  // ❸ RTK Query の設定
  const {
    data,        // { usedItems: number }
    refetch,     // 手動で再フェッチする関数
    isSuccess,   // 成功したかどうか
  } = useGetUsedItemsQuery(undefined, {
    skip: !(userInfo?.subscription?.usedItems > 0),        // usedItems が 1以上 のときだけ有効化
    refetchOnMountOrArgChange: false,                     // 自動フェッチを抑制
  });

  // ——————————————————————————————————————————————————————————————
  // ページ遷移ごとに usedItems をチェックして最新化
  useEffect(() => {
    if (userInfo?.subscription?.usedItems > 0) {
      console.log('usedItems をリフェッチ');
      refetch();
    }
  }, [location.pathname, refetch, userInfo?.subscription?.usedItems]);

  // API コール成功＆ usedItems が 0 なら Redux と localStorage を更新
  useEffect(() => {
    if (isSuccess && data?.usedItems === 0) {
      console.log(`usedItems: ${data?.usedItems}`);
      console.log('usedItems を Redux と localStorage に保存');
      dispatch(updateUsedItems(0));
    }
  }, [isSuccess, data, dispatch]);
  // ——————————————————————————————————————————————————————————————

  // ❹ Google Analytics の初期化（初回のみ）
  useEffect(() => {
    if (GA_TRACKING_ID) {
      ReactGA.initialize(GA_TRACKING_ID);
      if (GA_DEBUG) {
        try {
          ReactGA.gtag('set', 'debug_mode', true);
        } catch (_) {}
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ❺ ページビュー送信（ルート変更時）
  useEffect(() => {
    if (GA_TRACKING_ID) {
      ReactGA.send({ hitType: 'pageview', page: location.pathname, debug_mode: GA_DEBUG });
    }
  }, [location.pathname]);

  const currentUrl = `${FRONTEND_BASE_URL}${location.pathname}`;
  const computedCanonical = currentUrl.replace(/\/$/, '');

  return (
    <DeviceProvider>
      <JsonLdProvider>
        <Meta canonical={computedCanonical} />
        <CombinedJsonLd />
        <Header />
        <main>
          <Outlet />
        </main>
        <Footer />
        <ToastContainer />
      </JsonLdProvider>
    </DeviceProvider>
  );
};

export default App;